import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";

/**
 * Hook to manage proposal details, versions, and actions
 */
export const useProposalDetail = (id, showNotification) => {
	const navigate = useNavigate();
	const [proposal, setProposal] = useState(null);
	const [items, setItems] = useState([]);
	const [accessToken, setAccessToken] = useState(null);
	const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
	const [proposalResponse, setProposalResponse] = useState(null);
	const [loading, setLoading] = useState(true);
	const [sendingEmail, setSendingEmail] = useState(false);
	const [cloning, setCloning] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [versions, setVersions] = useState([]);
	const [versionModalOpen, setVersionModalOpen] = useState(false);
	const [selectedVersion, setSelectedVersion] = useState(null);
	const [loadingVersion, setLoadingVersion] = useState(false);

	const fetchProposal = useCallback(async () => {
		try {
			setLoading(true);
			const response = await client.get(`/proposals/${id}`);
			setProposal(response.data.proposal);
			setItems(response.data.items || []);
			setAccessToken(response.data.access_token || null);
			setTokenExpiresAt(response.data.token_expires_at || null);
			setProposalResponse(response.data.response || null);
		} catch (error) {
			console.error("Error fetching proposal:", error);
			if (showNotification)
				showNotification("Erro ao carregar proposta", "error");
		} finally {
			setLoading(false);
		}
	}, [id, showNotification]);

	const fetchVersions = useCallback(async () => {
		try {
			const response = await client.get(`/proposals/${id}/versions`);
			setVersions(response.data || []);
		} catch (error) {
			console.error("Error fetching versions:", error);
		}
	}, [id]);

	useEffect(() => {
		fetchProposal();
		fetchVersions();
	}, [fetchProposal, fetchVersions]);

	const handleSendEmail = async () => {
		try {
			setSendingEmail(true);
			await client.post(`/proposals/${id}/send-email`);
			if (showNotification) showNotification("Email enviado com sucesso!");
		} catch (error) {
			console.error("Error sending email:", error);
			if (showNotification) {
				showNotification(
					error.response?.data?.error || "Erro ao enviar email",
					"error",
				);
			}
		} finally {
			setSendingEmail(false);
		}
	};

	const handleClone = async () => {
		if (!window.confirm("Deseja realmente clonar esta proposta?")) return;
		try {
			setCloning(true);
			const response = await client.post(`/proposals/${id}/clone`);
			if (showNotification) showNotification("Proposta clonada com sucesso!");
			navigate(`/admin/proposals/${response.data.proposal.id}`);
		} catch (error) {
			console.error("Error cloning proposal:", error);
			if (showNotification) {
				showNotification(
					error.response?.data?.error || "Erro ao clonar proposta",
					"error",
				);
			}
		} finally {
			setCloning(false);
		}
	};

	const handleDelete = async () => {
		if (
			!window.confirm(
				"Deseja realmente excluir esta proposta? Esta ação não pode ser desfeita.",
			)
		)
			return;
		try {
			setDeleting(true);
			await client.delete(`/proposals/${id}`);
			if (showNotification) showNotification("Proposta excluída com sucesso!");
			navigate("/admin/proposals");
		} catch (error) {
			console.error("Error deleting proposal:", error);
			if (showNotification) {
				showNotification(
					error.response?.data?.error || "Erro ao excluir proposta",
					"error",
				);
			}
		} finally {
			setDeleting(false);
		}
	};

	const handleViewVersion = async (versionId) => {
		try {
			setLoadingVersion(true);
			setVersionModalOpen(true);
			const response = await client.get(
				`/proposals/${id}/versions/${versionId}`,
			);
			setSelectedVersion(response.data);
		} catch (error) {
			console.error("Error fetching version:", error);
			if (showNotification)
				showNotification("Erro ao carregar versão", "error");
			setVersionModalOpen(false);
		} finally {
			setLoadingVersion(false);
		}
	};

	const proposalLink = accessToken
		? `${window.location.origin}/proposta/${accessToken}`
		: null;

	const getExpirationInfo = () => {
		if (!tokenExpiresAt) return null;
		const now = new Date();
		const expiresAt = new Date(tokenExpiresAt);
		const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

		if (daysRemaining <= 0)
			return { label: "Expirada", variant: "default", days: daysRemaining };
		if (daysRemaining < 7)
			return {
				label: `Expira em ${daysRemaining} dia(s)`,
				variant: "rejected",
				days: daysRemaining,
			};
		if (daysRemaining <= 14)
			return {
				label: `Expira em ${daysRemaining} dia(s)`,
				variant: "pending",
				days: daysRemaining,
			};
		return {
			label: `Expira em ${daysRemaining} dia(s)`,
			variant: "accepted",
			days: daysRemaining,
		};
	};

	return {
		proposal,
		items,
		proposalLink,
		tokenExpiresAt,
		proposalResponse,
		loading,
		sendingEmail,
		cloning,
		deleting,
		versions,
		versionModalOpen,
		setVersionModalOpen,
		selectedVersion,
		setSelectedVersion,
		loadingVersion,
		handleSendEmail,
		handleClone,
		handleDelete,
		handleViewVersion,
		expirationInfo: getExpirationInfo(),
	};
};
